/**
 * 功法面板
 * 查看当前功法、灵根匹配度、升级、切换功法列表
 */

import type { PlayerState } from '../engine/Player';
import { getPlayerTechnique, calcMatchScore } from '../engine/TechniqueSystem';
import { TECHNIQUES, getLayerMultiplier, getUpgradeCost } from '../data/techniques';
import type { Technique } from '../data/techniques';

export type TechniqueAction = 'upgrade' | 'learn' | 'close';
export interface TechniqueActionPayload {
  action: TechniqueAction;
  techId?: string;
}

const ALL_ELEMENTS = ['金', '木', '水', '火', '土'] as const;

export class TechniquePanel {
  private overlay: HTMLElement | null = null;
  private onAction: ((payload: TechniqueActionPayload) => void) | null = null;

  setOnAction(callback: (payload: TechniqueActionPayload) => void): void {
    this.onAction = callback;
  }

  open(player: PlayerState): void {
    if (this.overlay) this.overlay.remove();

    const tech = getPlayerTechnique(player);
    const layer = player.techniqueLayer ?? 1;
    const layerMult = getLayerMultiplier(layer);

    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';

    // 当前功法区
    let currentHtml = '';
    if (tech) {
      const match = calcMatchScore(player.spiritualRoot, tech);
      const canUpgrade = layer < tech.maxLayer;
      const cost = getUpgradeCost(layer);
      const affinities = ALL_ELEMENTS.map((e) => {
        const val = tech.affinity[e];
        const isMatch = player.spiritualRoot.elements.includes(e as any);
        return `<span style="color:${isMatch ? 'var(--text-gold)' : 'var(--text-secondary)'};font-size:0.85em;">${e}:${val.toFixed(1)}</span>`;
      }).join(' ');

      currentHtml = `
        <div style="background:rgba(200,160,60,0.08);border:1px solid rgba(200,160,60,0.2);border-radius:6px;padding:12px;margin-bottom:12px;">
          <div style="font-weight:bold;color:var(--text-gold);">${tech.name} <span style="font-size:0.8em;">[${tech.grade}]</span></div>
          <div style="font-size:0.8em;color:var(--text-secondary);margin:4px 0;">${tech.desc}</div>
          <div style="margin:6px 0;">${affinities}</div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
            <span style="font-size:0.9em;">🔮 匹配度：<b style="color:var(--text-gold)">${(match*100).toFixed(0)}%</b> | 第${layer}/${tech.maxLayer}层 (×${layerMult.toFixed(2)})</span>
            ${canUpgrade
              ? `<button class="btn btn-small btn-primary" id="tech-upgrade">⬆️ 升级 (${cost}灵石)</button>`
              : '<span style="color:var(--text-secondary);font-size:0.8em;">已满层</span>'}
          </div>
        </div>
      `;
    }

    // 可学习功法列表
    let listHtml = '<div class="panel-subtitle">📚 功法库</div>';
    for (const t of TECHNIQUES) {
      if (t.id === player.techniqueId) continue;
      if (t.price === 0) continue; // 非卖品不在列表显示
      const match = calcMatchScore(player.spiritualRoot, t);
      const matchColor = match >= 1.0 ? 'var(--success)' : match >= 0.5 ? 'var(--text-secondary)' : 'var(--danger)';
      listHtml += `
        <div class="tech-item">
          <div>
            <span style="font-weight:bold;">${t.name}</span>
            <span style="font-size:0.75em;color:var(--text-secondary);">[${t.grade}]</span>
            <span style="font-size:0.8em;color:${matchColor};">匹配${(match*100).toFixed(0)}%</span>
          </div>
          <div style="font-size:0.75em;color:var(--text-secondary);">${t.desc.slice(0, 25)}...</div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:4px;">
            <span style="color:var(--text-gold);font-size:0.85em;">💰${t.price}</span>
            <button class="btn btn-small" data-tech="${t.id}">学习</button>
          </div>
        </div>
      `;
    }

    this.overlay.innerHTML = `
      <div class="modal" style="max-width:480px;max-height:80vh;overflow-y:auto;">
        <h2>📖 功法</h2>
        ${currentHtml}
        ${listHtml}
        <button class="btn btn-small" id="tech-close" style="margin-top:12px;">关闭</button>
      </div>
    `;

    document.body.appendChild(this.overlay);

    document.getElementById('tech-upgrade')?.addEventListener('click', () => {
      if (this.onAction) this.onAction({ action: 'upgrade' });
    });

    this.overlay.querySelectorAll('button[data-tech]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const techId = (btn as HTMLElement).dataset.tech!;
        if (this.onAction) this.onAction({ action: 'learn', techId });
      });
    });

    document.getElementById('tech-close')?.addEventListener('click', () => {
      this.close();
      if (this.onAction) this.onAction({ action: 'close' });
    });

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
        if (this.onAction) this.onAction({ action: 'close' });
      }
    });
  }

  close(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  isOpen(): boolean {
    return this.overlay !== null;
  }
}
